import { useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { toast } from "sonner";
import { useCustomer } from "@/lib/customerAuth";
import { formatErr } from "@/lib/api";
import { UserPlus, Mail, Lock, User as UserIcon } from "lucide-react";

export default function Register() {
  const { register, signIn } = useCustomer();
  const nav = useNavigate();
  const [name, setName] = useState("");
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [busy, setBusy] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    if (password.length < 8) { toast.error("Password must be at least 8 characters"); return; }
    setBusy(true);
    try {
      await register(name, email, password);
      toast.success("Account created — welcome!");
      nav("/my-bookings");
    } catch (err) {
      toast.error(formatErr(err.response?.data?.detail) || "Registration failed");
    } finally { setBusy(false); }
  };

  return (
    <div className="max-w-md mx-auto px-6 py-16">
      <div className="text-xs uppercase tracking-widest text-[#D71920] font-bold text-center">Create account</div>
      <h1 className="font-display text-4xl uppercase mt-2 text-center">Sign up</h1>
      <p className="text-sm text-[#555] text-center mt-3">Track your bookings and re-book with one click.</p>

      <form onSubmit={submit} className="ff-card p-8 mt-8 space-y-4" data-testid="register-form">
        <div>
          <label className="text-xs uppercase tracking-widest text-[#666] font-bold">Full name</label>
          <div className="relative mt-1">
            <UserIcon className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-[#999]" />
            <input required value={name} onChange={e => setName(e.target.value)} className="ff-input pl-9" data-testid="register-name" />
          </div>
        </div>
        <div>
          <label className="text-xs uppercase tracking-widest text-[#666] font-bold">Email</label>
          <div className="relative mt-1">
            <Mail className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-[#999]" />
            <input type="email" required value={email} onChange={e => setEmail(e.target.value)} className="ff-input pl-9" data-testid="register-email" />
          </div>
        </div>
        <div>
          <label className="text-xs uppercase tracking-widest text-[#666] font-bold">Password (min 8 chars)</label>
          <div className="relative mt-1">
            <Lock className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-[#999]" />
            <input type="password" required minLength={8} value={password} onChange={e => setPassword(e.target.value)} className="ff-input pl-9" data-testid="register-password" />
          </div>
        </div>
        <button type="submit" disabled={busy} className="ff-btn-primary w-full" data-testid="register-submit">
          <UserPlus className="w-4 h-4" /> {busy ? "Creating…" : "Create account"}
        </button>
      </form>

      <div className="relative my-6 text-center">
        <div className="absolute inset-0 flex items-center"><div className="w-full border-t border-[#e5e5e5]" /></div>
        <span className="relative bg-white px-3 text-xs uppercase tracking-widest text-[#999]">Or</span>
      </div>
      <button onClick={signIn} className="w-full border-2 border-[#111] px-6 py-3 text-sm uppercase tracking-widest font-bold hover:bg-[#111] hover:text-white transition" data-testid="google-signup">
        Sign up with Google
      </button>

      <p className="text-center text-sm text-[#555] mt-6">
        Already have an account? <Link to="/login" className="text-[#D71920] font-bold hover:underline" data-testid="go-login">Sign in</Link>
      </p>
    </div>
  );
}
